/**
 * English `plytix.brand` on translated storefronts: fetches `plytix-primary-brand-bridge` section
 * using the primary locale URL. `Theme` comes from `const Theme` in scripts.liquid (not `window.Theme`).
 */
const SECTION_ID = 'plytix-primary-brand-bridge';
const HYDRATE_DEBOUNCE_MS = 50;

const brandHtmlCache = new Map();

/** @returns {any | null} */
function getTheme() {
  return typeof Theme !== 'undefined' ? /** @type {any} */ (Theme) : null;
}

/**
 * @param {string} primaryRoot
 * @param {string} handle
 */
function buildSectionUrl(primaryRoot, handle) {
  const root = (primaryRoot || '/').replace(/\/?$/, '/');
  const url = new URL(`${root}products/${encodeURIComponent(handle)}`, window.location.origin);
  url.searchParams.set('section_id', SECTION_ID);
  return url.toString();
}

/**
 * @param {string} html
 * @returns {{ brand: string, brandHandle: string } | null}
 */
function parseBridgeResponse(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const el = doc.querySelector('[data-plytix-primary-brand-response]');
  if (!el?.textContent) return null;
  try {
    return /** @type {{ brand: string, brandHandle: string }} */ (JSON.parse(el.textContent.trim()));
  } catch {
    return null;
  }
}

/**
 * @param {string} handle
 * @param {string} primaryRoot
 */
function fetchPrimaryBrand(handle, primaryRoot) {
  if (!handle) return Promise.resolve(null);
  if (brandHtmlCache.has(handle)) return brandHtmlCache.get(handle);

  const w = /** @type {Window & { __plytixBrandSectionPromises?: Map<string, Promise<string | null>> }} */ (window);
  const early = w.__plytixBrandSectionPromises?.get(handle);

  const fromText = (/** @type {Promise<string | null>} */ p) =>
    p
      .then((html) => (html ? parseBridgeResponse(html) : null))
      .catch(() => null);

  const promise = early
    ? fromText(early)
    : fromText(
        fetch(buildSectionUrl(primaryRoot, handle), {
          credentials: 'same-origin',
          priority: 'high',
        }).then((r) => (r.ok ? r.text() : null))
      );

  brandHtmlCache.set(handle, promise);
  return promise;
}

function hydrateBrandTargets() {
  const cfg = getTheme()?.plytixPrimaryBrand;
  if (!cfg?.fetch) return;

  /** @type {NodeListOf<HTMLElement>} */
  const nodes = document.querySelectorAll('[data-plytix-primary-brand]');
  if (!nodes.length) return;

  const primaryRoot = cfg.primaryLocaleRoot || '/';
  const collectionsBase = getTheme()?.routes?.collections_url || '/collections';

  /** @type {Map<string, HTMLElement[]>} */
  const byHandle = new Map();
  for (const el of nodes) {
    if (!el.classList.contains('plytix-primary-brand--pending')) continue;
    const h = el.dataset.productHandle;
    if (!h) continue;
    const list = byHandle.get(h) || [];
    list.push(el);
    byHandle.set(h, list);
  }

  if (!byHandle.size) return;

  for (const [handle, elements] of byHandle) {
    fetchPrimaryBrand(handle, primaryRoot).then(
      /** @param {{ brand: string, brandHandle: string } | null} data */
      (data) => {
        for (const el of elements) {
          el.classList.remove('plytix-primary-brand--pending');
          if (!data?.brand) continue;
          const isLink = el.dataset.plytixPrimaryBrand === 'link' && el instanceof HTMLAnchorElement;
          if (isLink) {
            el.textContent = data.brand;
            if (data.brandHandle) {
              const base = collectionsBase.replace(/\/$/, '');
              el.href = `${base}/${data.brandHandle}`;
            }
          } else {
            el.textContent = data.brand;
          }
        }
      }
    );
  }
}

function init() {
  hydrateBrandTargets();
}

if (document.readyState === 'loading') {
  document.addEventListener('readystatechange', function onReady() {
    if (document.readyState === 'loading') return;
    document.removeEventListener('readystatechange', onReady);
    init();
  });
} else {
  init();
}

document.addEventListener('shopify:section:load', init);

/** @param {MutationRecord[]} mutations */
function addedPendingBrandSlots(mutations) {
  for (const m of mutations) {
    for (const n of m.addedNodes) {
      if (n.nodeType !== Node.ELEMENT_NODE) continue;
      const el = /** @type {Element} */ (n);
      if (el.matches?.('[data-plytix-primary-brand].plytix-primary-brand--pending')) return true;
      if (el.querySelector?.('[data-plytix-primary-brand].plytix-primary-brand--pending')) return true;
    }
  }
  return false;
}

let hydrateDebounceId = 0;
function startBrandSlotObserver() {
  if (!getTheme()?.plytixPrimaryBrand?.fetch || !document.body) return;

  new MutationObserver((mutations) => {
    if (!addedPendingBrandSlots(mutations)) return;
    window.clearTimeout(hydrateDebounceId);
    hydrateDebounceId = window.setTimeout(init, HYDRATE_DEBOUNCE_MS);
  }).observe(document.body, { childList: true, subtree: true });
}

if (document.body) {
  startBrandSlotObserver();
} else {
  document.addEventListener('DOMContentLoaded', startBrandSlotObserver, { once: true });
}
